// src/pages/Quotation/QuotationQuickVoucher.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Typography, Button, Spin, Divider, Table, App, Grid, Switch } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Big from "big.js";
import { useReactToPrint } from "react-to-print";
import ReportDom from "../../../components/common/ReportDom";
import { convertNumberToWords, downloadPdf, formatToCompanyTimezone } from "../../../lib/functions";
import { salesQuotationWithRecordApi } from "../../../apiservice/sales";
import { getInstitutionApi } from "../../../apiservice/setting";
import Swal from "sweetalert2";
import { selectAuthInfo } from "../../Auth/authSelectors";
import QuotationVoucherBody from "./QuotationVoucherBody";
import { useShareDocument } from "../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const QuotationQuickVoucher = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency;
  const { message } = App.useApp();

  const [voucher, setVoucher] = useState(null);
  const [institution, institutionSet] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const reportRef = useRef();
  

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Quotation_${voucher?.sale_order_no || "Voucher"}`,
  });

  useEffect(() => {
    if (!id) return;
    fetchVoucher();
    getInstitution();
  }, [id]);

  useEffect(() => {
    if (shareOpen && customerEmail && !email) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, email, setEmail]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const res = await salesQuotationWithRecordApi({
        sale_o_id: id,
        from: "voucher",
      });
      if (res?.data?.length > 0) setVoucher(res.data[0]);
      else message.warning("No quotation data found");
    } catch (err) {
      console.error(err);
      message.error("Failed to load quotation data");
    } finally {
      setLoading(false);
    }
  };

  let getInstitution = async () => {
      await getInstitutionApi()
        .then((res) => {
          
          institutionSet(res.data);
        })
        .catch((err) => {
          if (err.response?.data?.message) {
    
            Swal({ 
                title: err.response.data.message, 
                icon: "warning" 
            });
        } else {
            Swal({ 
                title: "Internal Error",  text: "If the issue persists, please contact our support team.", 
                icon: "warning" 
            });
        }
        });
    };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!voucher) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Quotation Found</Title>
        <Button onClick={() => navigate("/quotation/quotation")}>Back</Button>
      </div>
    );
  }

  const details = voucher.details || [];
  const grandTotal =
    Big(voucher.total_amount || 0 ).times(voucher.conversion_rate || 1).toNumber();

  // 🧾 columns for the on-screen AntD table
  const columns = [
    { title: "No.", render: (_, __, i) => i + 1, width: 60 },
    {
      title: "Item Name",
      dataIndex: "item_name",
      render: (_, record) => (
        <>
          <div>{record.item_name}</div>

          {record.item_description && (
            <div
              style={{
                fontSize: 12,
                color: "#666",
                whiteSpace: "pre-wrap",
              }}
            >
              {record.item_description}
            </div>
          )}
        </>
      ),
    },
    { title: "Rate", dataIndex: "item_rate", align: "right" },
    { title: "Per", dataIndex: "unit_symbol", align: "center" },
    { title: "QTY", dataIndex: "item_qty", align: "right" },
    { title: "Total", dataIndex: "item_total", align: "right", 
      render: (value) => Big(value || 0 ).toFixed(2), },

  ];

  const handleDownloadPDF = async () => {
    try {
      await downloadPdf(
        `/print/quotation/${id}`,
        `Quotation_${voucher?.sale_order_no}.pdf`
      );
    } catch (err) {
      console.error("Failed to download quotation PDF:", err);
      message.error("Failed to download PDF");
    }
  };

    // Terms & Condition section component
    const TermsSection = ({ voucher }) => {
    const renderBulletText = (text) => {
      if (!text) return null;

      return (
        <ul
          style={{
            paddingLeft: 24,
            marginTop: 8,
            marginBottom: 24,
          }}
        >
          {text
            .split("\n")
            .filter((line) => line.trim())
            .map((line, i) => (
              <li
                key={i}
                style={{
                  marginBottom: 10,
                  lineHeight: 1.8,
                  fontSize: 14,
                }}
              >
                {line}
              </li>
            ))}
        </ul>
      );
    };

    if (
      !voucher?.terms_conditions &&
      !voucher?.payment_terms
    ) {
      return null;
    }

    return (
      <div
        style={{
          marginTop: 40,
          padding: "20px 10px",
        }}
      >
        {voucher?.terms_conditions && (
          <>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 12,
                textAlign: "center",
              }}
              >
              Terms & Conditions
            </h2>

            {renderBulletText(voucher.terms_conditions)}
          </>
        )}

        {voucher?.payment_terms && (
          <>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 12,
                textAlign: "center",
              }}
            >
              Payment Terms
            </h2>

            {renderBulletText(voucher.payment_terms)}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: screens.xs ? 6 : 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/quotation/quotation")}
          >
            Back
          </Button>
        </Col>
        <Col>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <Switch
              checked={showStamp}
              onChange={setShowStamp}
              checkedChildren="Stamp"
              unCheckedChildren="Stamp"
            />

            <Switch
              checked={showSignature}
              onChange={setShowSignature}
              checkedChildren="Signature"
              unCheckedChildren="Signature"
            />

            <ShareDocumentButton
              disabled={!voucher}
              screens={screens}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/quotation/${id}`,
                  fileName: `Quotation_${
                    voucherNo || voucher.sale_order_no || "Voucher"
                  }.pdf`,
                  name: accName || voucher.acc_name || "",
                  docNo:
                    voucherNo ||
                    voucher.sale_order_no ||
                    "Voucher",
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/quotation/${id}`,
                  fileName: `Quotation_${
                    voucherNo || voucher.sale_order_no || "Voucher"
                  }.pdf`,
                  text: `Hello ${
                    accName || voucher.acc_name || ""
                  }, Your Quotation ${
                    voucherNo ||
                    voucher.sale_order_no ||
                    "Voucher"
                  } is ready. Download here:`,
                })
              }
            />

            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPDF}
              disabled={!voucher}
            >
              Download
            </Button>

            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={!voucher}
            >
              Print Voucher
            </Button>
          </div>
        </Col>
      </Row>

      {/* 🧾 On-screen preview */}
      <div
        style={{
          background: "white",
          padding: screens.xs ? 6 : 24,
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Title level={4} style={{ textAlign: "center" }}>
          QUOTATION VOUCHER
        </Title>

        <Row gutter={[screens.xs ? 6 : 24, 8]} style={{ marginTop: 20 }}>
          <Col xs={24} sm={12}>
            <p>
              <b>Customer/ Debtor Code:</b> {voucher.acc_code}
            </p>
            <p>
              <b>Name:</b> {voucher.acc_name}
            </p>
            {/* <p>
              <b>Institution:</b> {voucher.institution_name}
            </p> */}
            <p className="wrap-text">
              <b>Address:</b> {voucher.address}
            </p>
            <p>
              <b>Contact No:</b> +{voucher.contact_no}
            </p>
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: screens.xs ? "left" : "right" }}>
            <p>
              <b>Voucher No:</b> {voucher.sale_order_no}
            </p>
            <p>
              <b>Quotation Date:</b>{" "}
              {formatToCompanyTimezone(voucher.created_date)?.split(" ")[0]}
            </p>
            <p>
              <b>Quotation By:</b> {voucher.full_name}
            </p>
            <p className="wrap-text"><b>Address:</b> {institution?.street_address}</p>
          {institution?.phone_no &&<p><b>Contact No:</b> +{institution?.phone_no}</p>}
          {institution?.vat_license && <><p>
              <b>{institution?.country?.trim().toLowerCase() === "united arab emirates" 
                ? "TRN No: "
                : "Tax Reg No: "}{" "}</b>
              {institution?.vat_license}
            </p></>}
          </Col>
        </Row>

        <Divider />

        <Table
          columns={columns}
          dataSource={details}
          pagination={false}
          rowKey={(r) => r.item_id}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <b>Sub Total:</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {Big(voucher.sub_total || 0)?.toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>

              {isIndia && <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <b>  {authInfo?.country?.toLowerCase() === "india"
                    ? "GST Amount:"
                    : "VAT Amount:"}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  {Big(voucher.total_tax || 0 )?.toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>}

              {voucher.is_cal_type === "on_total" && (
                <>
                  {!isIndia && <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <b>  {authInfo?.country?.toLowerCase() === "india"
                        ? "GST Amount:"
                        : "VAT Amount:"}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {Big(voucher.total_tax || 0)?.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>}

                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <b>Discount Amount:</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {Big(voucher.total_discount || 0 )?.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
              {Big(voucher.other_service_cost || 0 ) > 0 && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5} align="right">
                    <b>Additional Expenses:</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {Big(voucher.other_service_cost || 0 ).toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}


              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <b>Grand Total:</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <b>{Big(grandTotal).toFixed(2)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />

        <Divider />

        <p>
          <b>In Word of Grand Total:</b>{" "}
          {convertNumberToWords(grandTotal, authInfo?.currency || "AED")}
        </p>
        <p>
          <b>Description:</b> {voucher.narration || ""}
        </p>

        {(showStamp || showSignature) && (
          <div className="invoice-footer-section">
            <div className="footer-right-group">
              {showStamp && institution?.stamp && (
                <div className="stamp-block">
                  <img src={institution.stamp} alt="Stamp" />
                </div>
              )}

              {showSignature && institution?.signature && (
                <div className="signature-block">
                  <img src={institution.signature} alt="Signature" />
                  <div className="sign-line-text">Authorized Signature</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{
          pageBreakBefore: "always",
          maxWidth: "900px",
          margin: "0 auto",
          paddingTop: "40px",
        }}>
        {voucher.show_terms_conditions === "yes" && (
          <TermsSection voucher={voucher} />
        )}
        </div>  

      </div>

      {/* 🖨️ Hidden printable version */}
      {/* <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Quotation Voucher"
          filters={{
            fromDate: voucher.created_date,
            toDate: voucher.created_date,
          }}
          renderTable={() => (
            <>
            <Table
              columns={columns}
              dataSource={details}
              pagination={false}
              rowKey={(r) => r.item_id}
              bordered
              size="small"
              summary={() => (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <b>Sub Total:</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {Big(voucher.sub_total || 0).toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  {voucher.is_cal_type === "on_total" && (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={5}
                          align="right"
                        >
                          <b>  {authInfo?.country?.toLowerCase() === "india"
                            ? "GST Amount:"
                            : "VAT Amount:"}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          {Big(voucher.total_tax || 0).toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      <Table.Summary.Row>
                        <Table.Summary.Cell
                          index={0}
                          colSpan={5}
                          align="right"
                        >
                          <b>Discount Amount:</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">
                          {Big(voucher.total_discount || 0).toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  )}
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <b>Grand Total ({currency}):</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <b>{Big(grandTotal).toFixed(2)}</b>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
              


            />
            <div style={{ marginTop: "10mm" }}>
      <p>
        <b>In Word of Grand Total:</b>{" "}
        {convertNumberToWords(grandTotal, authInfo?.currency || "INR")}
      </p>

      <p>
        <b>Description:</b> {voucher.narration || ""}
      </p>
      <div 
      style={{
        pageBreakBefore: "always",
        maxWidth: "900px",
        margin: "0 auto",
        paddingTop: "40px",
      }}>
      {voucher.show_terms_conditions === "yes" && (
        <TermsSection voucher={voucher} />
      )}
      </div>
    </div>
            </>
          )}
          // extraSections={[
          //   {
          //     title: "Summary",
          //     columns: [
          //       { title: "In Word", dataIndex: "inWord" },
          //       { title: "Description", dataIndex: "desc" },
          //     ],
          //     data: [
          //       {
          //         inWord: convertNumberToWords(
          //           grandTotal,
          //           authInfo?.currency || "INR"
          //         ),
          //         desc: voucher.narration || "",
          //       },
          //     ],
          //   },
          // ]}
        />

        
      </div> */}
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
        }}
      >
        <ReportDom
          ref={reportRef}
          mode={showSignature ? "signatureOn" : showStamp ? "signatureOn" : ""}
          renderTable={() => (
            <QuotationVoucherBody
              id={id}
              printMode
              showStamp={showStamp}
              showSignature={showSignature}
            />
          )}
        />
      </div>
    </div>
  );
};

export default QuotationQuickVoucher;
